import React, { useState, useEffect, useCallback } from 'react';
import { useRecords } from '../../hooks/useRecords';
import { useAppContext } from '../../contexts/Storage';
import { toast } from 'react-toastify';

interface ManagePermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordId: number;
  recordName: string;
}

interface UserPermission {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  can_read: boolean;
  can_write: boolean;
}

const ManagePermissionsModal: React.FC<ManagePermissionsModalProps> = ({
  isOpen,
  onClose,
  recordId,
  recordName
}) => {
  const { token, user } = useAppContext();
  const { 
    getRecordUsers, 
    grantPermission, 
    removeUserFromRecord, 
    isManagingPermissions 
  } = useRecords();
  
  const [users, setUsers] = useState<UserPermission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const loadRecordUsers = useCallback(async () => {
    if (!token || !user?.id) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await getRecordUsers(recordId);
      
      if (result.success && result.data) {
        // Transform the API response to our UserPermission format
        const transformedUsers = result.data.users.map(apiUser => ({
          id: apiUser.id,
          username: apiUser.username,
          email: apiUser.email,
          first_name: apiUser.first_name,
          last_name: apiUser.last_name,
          can_read: apiUser.pivot.can_read === 1,
          can_write: apiUser.pivot.can_write === 1
        }));
        
        setUsers(transformedUsers);
      } else {
        setError(result.error || 'Failed to load users');
        toast.error(result.error || 'Failed to load users');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token, user, getRecordUsers, recordId]);

  useEffect(() => {
    if (isOpen && recordId) {
      loadRecordUsers();
    }
  }, [isOpen, recordId, loadRecordUsers]);

  const handlePermissionChange = async (userId: number, field: 'can_read' | 'can_write', value: boolean) => {
    if (!token || !user?.id) return;
    
    try {
      const userToUpdate = users.find(u => u.id === userId);
      if (!userToUpdate) return;
      
      const updatedPermissions = {
        record_id: recordId,
        target_user_id: userId,
        can_read: field === 'can_read' ? value : userToUpdate.can_read,
        can_write: field === 'can_write' ? value : userToUpdate.can_write
      };
      
      const result = await grantPermission(userId, updatedPermissions);
      
      if (result.success) {
        // Update local state
        setUsers(prev => prev.map(u => 
          u.id === userId 
            ? { 
                ...u, 
                [field]: value 
              } 
            : u
        ));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      toast.error(`Failed to update permissions: ${errorMessage}`);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (!token || !user?.id) return;
    
    if (!window.confirm('Are you sure you want to remove this user from the record?')) {
      return;
    }
    
    try {
      const result = await removeUserFromRecord(user.id, recordId, userId);
      
      if (result.success) {
        // Remove user from local state
        setUsers(prev => prev.filter(u => u.id !== userId));
        toast.success('User removed successfully');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      toast.error(`Failed to remove user: ${errorMessage}`);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const submitting = isManagingPermissions || isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-light rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] h-full overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative p-6 pb-2 border-b border-grey_light">
          <div>
            <h3 className="text-xl text-dark">Manage Permissions</h3>
            <p className="text-grey_dark mt-1 text-sm">
              Manage user permissions for: <span className="font-medium text-primary">{recordName}</span>
            </p>
          </div>

          <button
            onClick={onClose}
            className="absolute top-0 right-2 text-grey_dark hover:text-dark text-2xl p-2"
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-danger">{error}</p>
              <button 
                onClick={loadRecordUsers}
                className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary_dark disabled:opacity-50"
                disabled={submitting}
              >
                Retry
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-grey_dark">No users found for this record.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-grey_light">
                  <thead>
                    <tr className="bg-grey_light bg-opacity-30">
                      <th className="px-4 py-3 text-left text-xs font-medium text-grey_dark uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-grey_dark uppercase tracking-wider">
                        Can Read
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-grey_dark uppercase tracking-wider">
                        Can Write
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-grey_dark uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-grey_light">
                    {users.map((userItem) => (
                      <tr key={userItem.id} className="hover:bg-grey_light hover:bg-opacity-20">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-dark">
                              {userItem.first_name} {userItem.last_name}
                            </p>
                            <p className="text-xs text-grey_dark">{userItem.email}</p>
                            <p className="text-xs text-grey_dark">@{userItem.username}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={userItem.can_read}
                              onChange={(e) => handlePermissionChange(userItem.id, 'can_read', e.target.checked)}
                              className="sr-only peer"
                              disabled={submitting}
                            />
                            <div className="relative w-11 h-6 bg-grey_light peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={userItem.can_write}
                              onChange={(e) => handlePermissionChange(userItem.id, 'can_write', e.target.checked)}
                              className="sr-only peer"
                              disabled={submitting}
                            />
                            <div className="relative w-11 h-6 bg-grey_light peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleRemoveUser(userItem.id)}
                            className="px-3 py-1 text-xs bg-danger text-white rounded hover:bg-danger_dark disabled:opacity-50"
                            disabled={submitting || userItem.id === user?.id}
                            title={userItem.id === user?.id ? "Cannot remove yourself" : "Remove user from record"}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Permission Guide</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <span className="font-medium">Can Read:</span> User can view the record and its transactions</li>
                  <li>• <span className="font-medium">Can Write:</span> User can create, edit, and delete transactions</li>
                  <li>• Changes are saved automatically when toggling checkboxes</li>
                  <li>• You cannot remove yourself from the record</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 pt-4 border-t border-grey_light flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-grey_light text-grey_dark rounded hover:bg-grey_dark hover:text-light disabled:opacity-50"
            disabled={submitting}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagePermissionsModal;